import { useCallback, useEffect } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { PRIMARY_HTML_ELEMENT_ID } from '../../config/constants';
import { analytics } from '../../services/analytics';
import { useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import { scrollTo } from '../../utils/scrollTo';
import { Balance } from '../BalancePanel/Balance';
import { BalancePanel } from '../BalancePanel/BalancePanel';
import { CoinTypeSwitcher } from '../CoinTypeSwitcher/CoinTypeSwitcher';
import { RiveCoinTypeSwitcher } from '../RiveCoinTypeSwitcher/RiveCoinTypeSwitcher';
import { AppLogo } from '../SVGIcons/AppLogo/AppLogo';

import { CCC_HEADER_CLASSNAME } from './constants';
import HeaderCrownBadge from './HeaderCrownBadge';
import classes from './Headers.module.scss';
import { MenuToggle } from './MenuToggle';
import { useHeaderHeight } from './useHeaderHeight';

const COLORFUL_LOGO_WIDTH = 76;

const HomeHeader = observer(() => {
  const {
    uiStore,
    categoriesStore,
    loyaltyProgramStore,
    appStore,
    userStore,
    coinTypeStore,
    bingoStore,
    actionEventTriggerStore,
  } = useStores();
  const preventPurchase = useFlag('prevent_purchase', false);
  const lobbyRedesignFlag = useFlag('ui_redesign_lobby', false);
  const lobbyRedesign = lobbyRedesignFlag && !uiStore.openFullPageDialog;
  const loyaltyProgramFlag = useFlag('loyalty_program', true);
  const { playingCoinTutorial } = actionEventTriggerStore;

  const { headerHeight } = useHeaderHeight();

  useEffect(() => {
    if (headerHeight) {
      categoriesStore.setHeaderHeight(headerHeight);
    }
  }, [categoriesStore, headerHeight]);

  const handleBalanceClick = useCallback(
    (clickId: string) => {
      if (uiStore.openFullPageDialog === 'store') {
        uiStore.closeFullPageDialog();
        return;
      }
      void analytics.sendEvent('purchase', {
        step_name: 'store',
        event_action: 'click',
        triggered_by: 'click',
        store_type: 'store',
        button_location: 'header',
        store_segment: loyaltyProgramStore.currentTierKey,
        click_id: clickId,
        store_key: appStore.currentStore?.key || '',
        sign_up_date: userStore.profile?.createdAt || '',
      });
      if (lobbyRedesignFlag) {
        void analytics.sendEvent('store_section', {
          event_action: 'click',
          event_category: 'lobby_page',
        });
      }
      uiStore.setOpenFullPageDialog(preventPurchase ? 'prevent_purchase' : 'store');
    },
    [
      uiStore,
      loyaltyProgramStore.currentTierKey,
      appStore.currentStore?.key,
      userStore.profile?.createdAt,
      lobbyRedesignFlag,
      preventPurchase,
    ],
  );

  if (bingoStore.isInsideBingoArea) {
    return null;
  }

  const onClickAppLogo = () => {
    scrollTo(PRIMARY_HTML_ELEMENT_ID, 0);
  };

  const disableControls =
    (uiStore.openFullPageDialog === 'loyalty_program' && loyaltyProgramStore.isLoyaltyProgramDialogOpenWithTutorial) ||
    (uiStore.openFullPageDialog &&
      ['new_tier_congrats', 'tier_monthly_bonus', 'tier_birthday_gift'].includes(uiStore.openFullPageDialog));

  return (
    <div className={classNames(lobbyRedesign && 'lobby-redesign')}>
      <div
        className={classNames(
          classes.header,
          CCC_HEADER_CLASSNAME,
          coinTypeStore.coinType === 'GC' ? classes.header_gc_redesign : classes.header_sc,
          lobbyRedesign && classes.headerLobbyRedesign,
        )}
      >
        {lobbyRedesign && (
          <div
            className={
              coinTypeStore.coinType === 'GC'
                ? classes.headerScrollBackgroundGCOuter
                : classes.headerScrollBackgroundSCOuter
            }
          >
            <div className={classes.headerScrollBackgroundInner} />
          </div>
        )}
        <div className={classes.top}>
          <div className={classes.logo}>
            <AppLogo
              withText
              alwaysColorful={lobbyRedesign}
              width={lobbyRedesign ? COLORFUL_LOGO_WIDTH : undefined}
              onClick={onClickAppLogo}
            />
          </div>
          <div className={classes.menuToggleWrapper}>
            <MenuToggle />
          </div>
        </div>
      </div>

      <div
        className={classNames(
          classes.controls,
          classes.homeControls,
          uiStore.CoinsAnimationVisible && classes.zfull,
          disableControls && classes.controlsDisabled,
        )}
      >
        {loyaltyProgramFlag && loyaltyProgramStore.userLoyalty?.currentTier && <HeaderCrownBadge />}
        <BalancePanel handleClick={handleBalanceClick}>
          <Balance />
        </BalancePanel>
        {playingCoinTutorial ? <RiveCoinTypeSwitcher /> : <CoinTypeSwitcher />}
      </div>
    </div>
  );
});

export default HomeHeader;
